import { useTranslation } from 'react-i18next';
import { Baby, BedDouble, Check, User, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import { LockIndicator } from '@/components/lock-indicator/lock-indicator';
import type { SubCase } from '@/lib/sub-cases/types';
import { estimateRooms, pnrLabel } from './group-utils';
import {
  STAY_PLAN_LABEL_KEY,
  STAY_PLAN_ORDER,
  STAY_PLAN_SHORT_KEY,
  type StayPlanType,
} from './stay-plan';
import type { GroupAssignment, GroupLock } from './types';

// A draggable PNR-group card with its stay-plan selector + per-group lock pill.
// Pure presentation — data + callbacks via props, no scope coupling.

export interface GroupCardProps {
  group: SubCase;
  plan: StayPlanType;
  lock: GroupLock;
  onPlanChange: (plan: StayPlanType) => void;
  assignment: GroupAssignment | undefined;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
}

export function GroupCard({
  group,
  plan,
  lock,
  onPlanChange,
  assignment,
  dragging,
  onDragStart,
  onDragEnd,
}: GroupCardProps) {
  const { t } = useTranslation('cases');
  const adults = group.passengers.filter((p) => p.paxType === 'ADT').length;
  const children = group.passengers.filter((p) => p.paxType === 'CHD').length;
  const infants = group.passengers.filter((p) => p.paxType === 'INF').length;
  const rooms = estimateRooms(group);
  const isHotel = plan === 'hotel';
  // Draggable only when this group is hotel-bound, unassigned, AND locked by me.
  const draggable = isHotel && !assignment && lock.isMine;

  return (
    <div
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData('text/plain', group.urn);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className={cn(
        'rounded-lg border p-3 transition-all',
        draggable ? 'cursor-grab hover:border-primary/50 active:cursor-grabbing' : 'opacity-90',
        dragging && 'opacity-50 ring-2 ring-primary',
        !isHotel && 'bg-muted/30',
      )}
      data-testid="group-card"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-sm font-medium">{pnrLabel(group.pnrUrn)}</span>
        {assignment ? (
          assignment.kind === 'held' ? (
            <Badge variant="info" size="sm">
              {t('group.held')}
            </Badge>
          ) : (
            <Badge variant={assignment.status === 'BOOKED' ? 'success' : 'warning'} size="sm">
              {assignment.status === 'BOOKED' ? <Check className="h-3 w-3" aria-hidden /> : null}
              {assignment.status === 'BOOKED' ? t('group.booked') : assignment.status}
            </Badge>
          )
        ) : null}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {adults > 0 ? (
          <span className="flex items-center gap-1">
            <User className="h-3.5 w-3.5" aria-hidden />
            {adults}
          </span>
        ) : null}
        {children > 0 ? (
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" aria-hidden />
            {t('group.children', { count: children })}
          </span>
        ) : null}
        {infants > 0 ? (
          <span className="flex items-center gap-1">
            <Baby className="h-3.5 w-3.5" aria-hidden />
            {infants}
          </span>
        ) : null}
        {isHotel ? (
          <span className="flex items-center gap-1">
            <BedDouble className="h-3.5 w-3.5" aria-hidden />
            {t('group.rooms', { count: rooms })}
          </span>
        ) : null}
      </div>

      {assignment ? (
        <p className="mt-2 truncate text-xs text-muted-foreground">
          {assignment.kind === 'held' ? t('group.heldAt') : t('group.bookedAt')}
          <span className="text-foreground">{assignment.hotelName}</span>
        </p>
      ) : null}

      {/* Stay plan */}
      <label className="mt-2 flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">{t('group.stayPlanLabel')}</span>
        <select
          value={plan}
          onChange={(e) => onPlanChange(e.target.value as StayPlanType)}
          disabled={!!assignment}
          aria-label={t('group.stayPlanFor', { pnr: pnrLabel(group.pnrUrn) })}
          className="flex-1 rounded-md border border-input bg-background px-2 py-1"
        >
          {STAY_PLAN_ORDER.map((p) => (
            <option key={p} value={p}>
              {t(STAY_PLAN_LABEL_KEY[p])}
            </option>
          ))}
        </select>
      </label>
      {!isHotel ? (
        <Badge variant="neutral" size="sm" className="mt-2">
          {t(STAY_PLAN_SHORT_KEY[plan])} · {t('group.noRoomNeeded')}
        </Badge>
      ) : (
        // Per-group lock (product-owner decision): acquire it to assign this
        // group. The pill shows Available / Editing · countdown / Locked by ….
        <div className="mt-2 flex items-center justify-between gap-2">
          <LockIndicator entityUrn={group.urn} />
          {!assignment ? (
            <span className="text-[11px] text-muted-foreground">
              {lock.isMine ? t('group.dragHint') : t('group.lockHint')}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
